import React, { useEffect, useState } from 'react';
import { Pencil, X } from 'lucide-react';
import { getClientAdmins, updateClient, updateClientAdminEmail } from '../../../services/superAdminService';

const inputClass =
  'h-9 rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30';

// Lets the super admin edit a client's company details and its admins' login emails.
export default function EditClientDrawer({ client, onClose, onSaved }) {
  const [form, setForm] = useState({ companyName: '', contactEmail: '', contactPhone: '' });
  const [admins, setAdmins] = useState([]);
  const [adminEmails, setAdminEmails] = useState({}); // empId -> edited email
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!client) return;
    setForm({
      companyName: client.companyName || '',
      contactEmail: client.contactEmail || '',
      contactPhone: client.contactPhone || '',
    });
    setError('');
    setMessage('');
    getClientAdmins(client.id)
      .then((data) => {
        setAdmins(data);
        setAdminEmails(Object.fromEntries(data.map((admin) => [admin.empId, admin.email || ''])));
      })
      .catch(() => setError('Failed to load the client admins'));
  }, [client]);

  if (!client) return null;

  const update = (field) => (e) => setForm((current) => ({ ...current, [field]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setSaving(true);
    try {
      const result = await updateClient(client.id, form);
      if (!result.success) {
        setError(result.message);
        return;
      }
      // Only admins whose email actually changed are saved.
      for (const admin of admins) {
        const email = (adminEmails[admin.empId] || '').trim();
        if (email === (admin.email || '')) continue;
        const adminResult = await updateClientAdminEmail(client.id, admin.empId, email);
        if (!adminResult.success) {
          setError(`${admin.name || 'Admin'}: ${adminResult.message}`);
          return;
        }
      }
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="fixed right-0 top-0 flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-border/80 bg-card shadow-lg">
        <div className="relative border-b border-border/80 bg-gradient-to-b from-primary/[0.05] to-card px-6 pb-5 pt-6">
          <button onClick={onClose} className="absolute right-4 top-4 text-muted-foreground hover:text-foreground">
            <X className="size-5" />
          </button>
          <div className="mb-3 flex size-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
            <Pencil className="size-5" />
          </div>
          <h2 className="text-base font-semibold text-foreground">Edit client</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Update the company&apos;s contact details and its admins&apos; login emails.
          </p>
        </div>

        <form onSubmit={handleSave} className="flex flex-1 flex-col gap-4 px-6 py-6">
          {error && (
            <div className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-2 text-sm text-[#b91c1c]">{error}</div>
          )}
          {message && (
            <div className="rounded-lg border border-[#bbf7d0] bg-[#f0fdf4] px-3 py-2 text-sm text-[#15803d]">{message}</div>
          )}

          <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
            <span>Company name<span className="text-[#ef4444]"> *</span></span>
            <input required value={form.companyName} onChange={update('companyName')} className={inputClass} />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
            Contact email
            <input type="email" value={form.contactEmail} onChange={update('contactEmail')} className={inputClass} />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
            Contact phone
            <input value={form.contactPhone} onChange={update('contactPhone')} className={inputClass} />
          </label>

          <div className="mt-2 border-t border-border/80 pt-4">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Admin login emails
            </p>
            {admins.length === 0 ? (
              <p className="text-sm text-muted-foreground">This client has no admin accounts.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {admins.map((admin) => (
                  <label key={admin.empId} className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
                    {admin.name || 'Admin'}
                    <input
                      type="email"
                      required
                      value={adminEmails[admin.empId] ?? ''}
                      onChange={(e) => setAdminEmails((current) => ({ ...current, [admin.empId]: e.target.value }))}
                      className={inputClass}
                    />
                  </label>
                ))}
                <p className="text-xs text-muted-foreground">The admin signs in (and receives OTP codes) with this email.</p>
              </div>
            )}
          </div>

          <div className="mt-auto flex flex-col gap-2 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="h-9 w-full rounded-lg bg-primary text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="h-9 w-full rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
